import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  useTheme,
} from '@mui/material';
import { Favorite } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import { BackToTop } from './BackToTop';
import { trackDonation } from '../../configuration/analytics';

const contentUseCase = new ContentUseCase();

export const DonationPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const { donationInfo, fundraiserInfo } = contentUseCase.getAllContent(language);
  const content = donationInfo;
  const fundraiserContent = fundraiserInfo;

  const handleDonationClick = (cause?: string) => {
    trackDonation(undefined, cause);
  };

  return (
    <Container maxWidth="xl" sx={{ py: theme.spacing(6) }}>
      <SEO page="donate" />
      <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ mb: theme.spacing(6) }}>
        {content.title}{' '}
        <Box
          component="img"
          src="/Light_blue_ribbon.svg.png"
          alt="Light Blue Awareness Ribbon"
          sx={{
            height: '60px',
            width: 'auto',
            marginLeft: theme.spacing(2),
            verticalAlign: 'middle',
          }}
        />
      </Typography>

      <Typography variant="h5" color="text.secondary" paragraph align="center" sx={{ mb: theme.spacing(8) }}>
        {content.subtitle}
      </Typography>

      {/* Main Donation Button */}
      <Box sx={{ textAlign: 'center', mb: theme.spacing(8) }}>
        <Button
          variant="contained"
          size="large"
          href={content.mainDonationButton.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleDonationClick('main_donation')}
          sx={{
            px: theme.spacing(6),
            py: theme.spacing(2),
            fontSize: '1.2rem',
            fontWeight: 600,
            mb: theme.spacing(4),
          }}
        >
          {content.mainDonationButton.title}
        </Button>

        {/* Betterplace Disclaimer */}
        {content.betterplaceDisclaimer && (
          <Box
            sx={{
              maxWidth: { xs: '100%', md: 800 },
              mx: 'auto',
              mt: theme.spacing(6),
            }}
          >
            <Box sx={{
              width: '60px',
              height: '2px',
              backgroundColor: 'primary.main',
              mx: 'auto',
              mb: theme.spacing(3),
            }} />
            <Typography variant="h6" gutterBottom align="center" sx={{ fontWeight: 600, color: 'text.primary', mb: theme.spacing(2) }}>
              {content.betterplaceDisclaimer.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ lineHeight: 1.8, maxWidth: { xs: '100%', md: 700 }, mx: 'auto' }}>
              {content.betterplaceDisclaimer.text}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Main Donation Cards */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: theme.spacing(4),
          justifyContent: 'center',
        }}>
          {donationInfo.causes.map((cause) => (
            <Card key={`cause-${cause.title.toLowerCase().replace(/\s+/g, '-')}`} sx={{ flex: 1, maxWidth: { lg: 500 }, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: theme.spacing(4), display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h4" component="h3" gutterBottom color="primary">
                  {cause.title}
                </Typography>
                <Typography variant="h6" paragraph sx={{ mb: theme.spacing(3), mt: theme.spacing(2), color: 'text.secondary' }}>
                  {cause.description}
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: theme.spacing(4), color: 'text.secondary', flex: 1 }}>
                  {cause.details}
                </Typography>
                <Box sx={{ textAlign: 'center', mt: 'auto' }}>
                  <Button
                    variant="contained"
                    size="large"
                    href={content.mainDonationButton.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleDonationClick(cause.title)}
                    sx={{ px: theme.spacing(4), py: theme.spacing(1.5) }}
                  >
                    {content.mainDonationButton.title}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Impact Statistics */}
      <Box sx={{ mb: theme.spacing(8) }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(6) }}>
          {content.impactSection.title}
        </Typography>

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: theme.spacing(4),
          justifyContent: 'center',
        }}>
          {content.impactSection.cards.map((card, index) => (
            <Card key={`impact-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`} sx={{ flex: 1, textAlign: 'center', maxWidth: { md: 350 } }}>
              <CardContent sx={{ p: theme.spacing(4) }}>
                <Favorite sx={{ fontSize: theme.spacing(6), color: index === 0 ? 'warning.main' : index === 1 ? 'info.main' : 'error.main', mb: theme.spacing(2) }} />
                <Typography variant="h5" component="h3" gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Link to ME Info Page */}
      <Box sx={{ textAlign: 'center', mb: theme.spacing(8) }}>
        <Typography variant="h4" gutterBottom>
          {content.learnMore.title}
        </Typography>
        <Typography variant="h6" paragraph sx={{ mb: theme.spacing(4), color: 'text.secondary' }}>
          {content.learnMore.subtitle}
        </Typography>
        <Button
          variant="outlined"
          size="large"
          component={Link}
          to="/me-info"
          sx={{ px: theme.spacing(4), py: theme.spacing(1.5) }}
        >
          {content.learnMore.button}
        </Button>
      </Box>

      <Box sx={{
        width: '100px',
        height: '2px',
        backgroundColor: 'primary.main',
        mx: 'auto',
        mb: theme.spacing(8),
      }} />

      {/* Call to Action */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          {fundraiserContent.donation.title}
        </Typography>
        <Typography variant="h6" paragraph color="text.secondary">
          {fundraiserContent.donation.subtitle}
        </Typography>
        <Typography variant="body1" paragraph sx={{ mb: theme.spacing(4), color: 'text.secondary', maxWidth: 800, mx: 'auto' }}>
          {fundraiserContent.donation.impact}
        </Typography>
        <Button
          variant="contained"
          size="large"
          href={content.mainDonationButton.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleDonationClick('final_cta')}
          sx={{ px: theme.spacing(6), py: theme.spacing(2) }}
        >
          {content.mainDonationButton.title}
        </Button>
      </Box>

      <BackToTop />
    </Container>
  );
};
